import { useRef, useState } from 'react';
import { Box, Button, Flex, Group, Paper, Stack, Text } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import PageLoader from 'src/UI/loader/PageLoader';
import { ClickableSectionTitle } from 'src/UI/page-nav/ClickableSectionTitle';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import useMemories from './useMemories';
import useSaveMemories from './useSaveMemories';

type MemoriesEditorProps = {
  title: string;
  description: string;
  emptyText: string;
  queryKey: string[];
  apiEndpoint: string;
  canEdit?: boolean;
};

export default function MemoriesEditor({
  title,
  description,
  emptyText,
  queryKey,
  apiEndpoint,
  canEdit = true,
}: MemoriesEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const editValueRef = useRef('');

  const { data, isLoading, isError } = useMemories(queryKey);
  const { mutateAsync: saveMemories, isPending: isSaving } = useSaveMemories(queryKey, apiEndpoint);

  const handleEdit = () => {
    editValueRef.current = data?.markdown ?? '';
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      await saveMemories(editValueRef.current);
      setIsEditing(false);
    } catch (error) {
      notifyAndLogError(
        'Failed to save memories',
        'An error occurred while saving memories.',
        error,
      );
    }
  };

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  if (isError) {
    return <Text c="red">Failed to load memories. Please try again later.</Text>;
  }

  const pencilIcon = <IconPencil size={20} />;

  return (
    <Paper variant="content" mb="md">
      <Group justify="space-between" mb="md">
        <ClickableSectionTitle fw={600} size="lg" data-testid="section-label">
          {title}
        </ClickableSectionTitle>
        {canEdit && (
          <Flex gap="xs">
            {isEditing && (
              <Button leftSection={pencilIcon} onClick={handleSave} loading={isSaving}>
                Save
              </Button>
            )}
            <Button
              variant="default"
              onClick={isEditing ? handleCancel : handleEdit}
              leftSection={!isEditing && pencilIcon}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
          </Flex>
        )}
      </Group>
      <Stack gap="xl">
        <Text size="sm" c="dimmed">
          {description}
        </Text>

        {isEditing ? (
          <MarkdownEditor
            defaultValue={editValueRef.current}
            onChange={value => {
              editValueRef.current = value;
            }}
            rootClass="compact"
          />
        ) : (
          <Box>
            {data?.markdown ? (
              <MarkdownView value={data.markdown} />
            ) : (
              <Text c="dimmed" fs="italic">
                {emptyText}
              </Text>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
